import React, { useEffect, useState } from 'react';
import { FormatCapability, CapabilitiesResponse } from '../types';
import { fetchCapabilities } from '../api/streamService';

interface FormatSelectorProps {
  serverHttpBase: string;
  selectedFormat: string;
  onFormatChange: (formatId: string) => void;
  onCapabilitiesLoad?: (capabilities: CapabilitiesResponse) => void;
  onError?: (error: string) => void;
}

export const FormatSelector: React.FC<FormatSelectorProps> = ({
  serverHttpBase,
  selectedFormat,
  onFormatChange,
  onCapabilitiesLoad,
  onError,
}) => {
  const [capabilities, setCapabilities] = useState<FormatCapability[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState<number | null>(null);

  const loadCapabilities = async () => {
    setLoading(true);
    try {
      const data = await fetchCapabilities(serverHttpBase);
      setCapabilities(data.formats);
      setLastFetch(Date.now());
      
      if (onCapabilitiesLoad) {
        onCapabilitiesLoad(data);
      }

      // Auto-select first format if none selected
      if (!selectedFormat && data.formats.length > 0) {
        onFormatChange(data.formats[0].formatId);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (onError) {
        onError(`Failed to load capabilities: ${message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCapabilities();
  }, [serverHttpBase]);

  const getFormatDisplayName = (capability: FormatCapability): string => {
    const { formatId, details } = capability;
    
    if (details.codec === 'text') {
      return `${formatId} (Text/STT)`;
    }
    
    return `${formatId} (${details.codec.toUpperCase()} ${details.sampleRate/1000}kHz, ${details.channels}ch)`;
  };

  const getFormatDescription = (capability: FormatCapability): string => {
    const { details } = capability;
    
    if (details.codec === 'text') {
      return 'Speech-to-Text transcription stream';
    }
    
    return `${details.codec.toUpperCase()} • ${details.sampleRate}Hz • ${details.channels} channel(s) • ${details.bitDepth}-bit`;
  };

  return (
    <div className="format-selector">
      <div className="format-selector-header">
        <label htmlFor="format-select">Audio Format:</label>
        <button 
          onClick={loadCapabilities} 
          disabled={loading}
          className="refresh-btn"
          title="Refresh capabilities"
        >
          {loading ? '⟳' : '↻'}
        </button>
      </div>

      <select
        id="format-select"
        value={selectedFormat}
        onChange={(e) => onFormatChange(e.target.value)}
        disabled={loading || capabilities.length === 0}
      >
        {capabilities.length === 0 && !loading && (
          <option value="">No formats available</option>
        )}
        {loading && (
          <option value="">Loading formats...</option>
        )}
        {capabilities.map((capability) => (
          <option key={capability.formatId} value={capability.formatId}>
            {getFormatDisplayName(capability)}
          </option>
        ))}
      </select>

      {selectedFormat && (
        <div className="format-details">
          {(() => {
            const selected = capabilities.find(c => c.formatId === selectedFormat);
            if (!selected) return null;
            
            return (
              <div className="format-info">
                <div className="format-description">
                  {getFormatDescription(selected)}
                </div>
                {selected.details.bitrate > 0 && (
                  <div className="format-bitrate">
                    Bitrate: {(selected.details.bitrate / 1000).toFixed(0)} kbps
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {lastFetch && (
        <div className="capabilities-info">
          <small>
            Capabilities loaded: {capabilities.length} formats available
            <br />
            Last updated: {new Date(lastFetch).toLocaleTimeString()}
          </small>
        </div>
      )}
    </div>
  );
};
